import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUp } from "lucide-react";
import GradientBackground from "@/components/GradientBackground";

export default function Datenschutz() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" data-testid="datenschutz-page">
      <GradientBackground />

      <nav className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-[#ff0090]/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Link>
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="bg-black/50 backdrop-blur-md border-2 border-[#ff0090]/40 rounded-3xl p-6 sm:p-10 shadow-xl shadow-[#ff0090]/10">
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8" data-testid="datenschutz-title">
            Datenschutzerklärung
          </h1>

          <div className="space-y-8 text-white/70">
            <section>
              <h2 className="font-['Outfit'] text-xl font-semibold text-white mb-3">1. Datenschutz auf einen Blick</h2>
              
              <h3 className="font-semibold text-white/90 mt-4 mb-2">Allgemeine Hinweise</h3>
              <p className="leading-relaxed text-sm">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              </p>

              <h3 className="font-semibold text-white/90 mt-4 mb-2">Datenerfassung auf unserer Website</h3>
              <p className="leading-relaxed text-sm">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
              </p>
            </section>

            <section>
              <h2 className="font-['Outfit'] text-xl font-semibold text-white mb-3">2. Allgemeine Hinweise und Pflichtinformationen</h2>
              
              <h3 className="font-semibold text-white/90 mt-4 mb-2">Datenschutz</h3>
              <p className="leading-relaxed text-sm">
                Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
              </p>

              <h3 className="font-semibold text-white/90 mt-4 mb-2">Hinweis zur verantwortlichen Stelle</h3>
              <p className="leading-relaxed text-sm">
                Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:<br /><br />
                Daniel Gucze<br />
                Grimmstraße 12<br />
                36381 Schlüchtern<br /><br />
                E-Mail: support@ucllegacy.com
              </p>
            </section>

            <section>
              <h2 className="font-['Outfit'] text-xl font-semibold text-white mb-3">3. Datenerfassung auf unserer Website</h2>
              
              <h3 className="font-semibold text-white/90 mt-4 mb-2">Cookies</h3>
              <p className="leading-relaxed text-sm">
                Unsere Internetseiten verwenden teilweise so genannte Cookies. Cookies richten auf Ihrem Rechner keinen Schaden an und enthalten keine Viren. Cookies dienen dazu, unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen.
              </p>

              <h3 className="font-semibold text-white/90 mt-4 mb-2">Server-Log-Dateien</h3>
              <p className="leading-relaxed text-sm">
                Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind: Browsertyp und Browserversion, verwendetes Betriebssystem, Referrer URL, Hostname des zugreifenden Rechners, Uhrzeit der Serveranfrage und IP-Adresse.
              </p>
            </section>

            <section>
              <h2 className="font-['Outfit'] text-xl font-semibold text-white mb-3">4. Ihre Rechte</h2>
              <p className="leading-relaxed text-sm">
                Sie haben jederzeit das Recht unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung, Sperrung oder Löschung dieser Daten zu verlangen. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#ff0090] text-white shadow-lg shadow-[#ff0090]/40 flex items-center justify-center transition-all duration-300 hover:bg-[#ff0090]/80 hover:scale-110 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        data-testid="scroll-to-top-btn"
        aria-label="Nach oben scrollen"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </div>
  );
}
